// Home: two ways to start an episode (a big Quick one, a smaller full one), the cast (with a link to the library), and the list of episodes.
import { useEffect, useState } from "react";
import { imageSrc } from "../library/images";
import type { EpisodeSummary } from "../model/episode";
import { api } from "./api";
import { forgetEpisode } from "./timeline/episodeStore";
import { useLibrary } from "../library/store";
import { Avatar, Body, Confirm, Icon, Page, Toast, fmt } from "./common";
import { account, clearStarted, signIn, signOut } from "./account";
import "./landing/landing.css";

export function Home({ onQuick, onNew, onOpen, onLibrary, onSignOut }: { onQuick: () => void; onNew: () => void; onOpen: (id: string) => void; onLibrary: () => void; onSignOut?: () => void }) {
  const cast = useLibrary().assets.filter(a => a.kind === "character").slice(0, 4);
  const [eps, setEps] = useState<EpisodeSummary[] | null>(null);
  const [deleting, setDeleting] = useState<EpisodeSummary | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => { api.list().then(l => setEps(l.filter(e => !e.demo))).catch(() => setEps([])); }, []);
  async function remove(e: EpisodeSummary) {
    setDeleting(null);
    try {
      forgetEpisode(e.id);
      await api.remove(e.id);
      setEps(list => list?.filter(x => x.id !== e.id) ?? null);
    } catch (err) { setError(`Couldn't delete: ${(err as Error).message}`); }
  }
  return (
    <Page className="home home-adv">
      <div className="home-head">
        <button className="wordmark-plain lp-home-mark" onClick={() => { location.hash = "#/welcome"; }} title="Landing page">sketchy</button>
        {!account() && <button className="btn lp-signin" onClick={() => { if (signIn()) location.reload(); }}>Sign in</button>}
      </div>
      <Body>
        <button className="new-episode" onClick={onQuick}>
          <span className="new-episode-plus"><Icon name="plus" size={22} /></span>
          <span className="new-episode-text"><b>Quick episode</b><span>Draw a scene, then record your lines</span></span>
        </button>
        <button className="new-episode full" onClick={onNew}>
          <span className="new-episode-text"><b>New episode</b><span>Full tools: references, styles, timeline</span></span>
        </button>
        <section className="section">
          <div className="cast-head">
            <h2>Your cast</h2>
            <button className="cast-link" onClick={onLibrary}>Characters, places and props <Icon name="chevron" size={16} /></button>
          </div>
          <div className="cast-row">
            {cast.map(a => (
              <button key={a.id} className="cast-one" onClick={onLibrary}><Avatar assetId={a.id} size={62} /><b>{a.name}</b></button>
            ))}
            <button className="cast-one new" onClick={onLibrary} aria-label="New character"><span className="cast-new"><Icon name="plus" size={22} /></span><b>New</b></button>
          </div>
        </section>
        <section className="section">
          <div className="section-head"><h2>Episodes</h2></div>
          {eps === null ? <p className="hint">Loading…</p> : eps.length === 0 ? <p className="hint">Nothing yet. Your episodes will show up here.</p> : (
            <div className="ep-grid">
              {eps.map(e => (
                <div key={e.id} className="ep-card">
                  <button className="ep-card-open" onClick={() => onOpen(e.id)}>
                    <span className="ep-cover">{e.picture && <img src={imageSrc(e.picture)} alt="" />}<span className="ep-dur">{fmt(e.duration)}</span></span>
                    <b>{e.title}</b>
                    <span className="ep-card-sub">{e.lines} {e.lines === 1 ? "line" : "lines"}</span>
                  </button>
                  <button className="ep-card-bin" onClick={() => setDeleting(e)} aria-label={`Delete ${e.title}`}><Icon name="trash" size={16} /></button>
                </div>
              ))}
            </div>
          )}
        </section>
        {account() && onSignOut && <button className="link-btn lp-signout" onClick={() => { signOut(); clearStarted(); onSignOut(); }}>Sign out</button>}
      </Body>
      {deleting && (
        <Confirm title={`Delete “${deleting.title}”?`} yes="Delete episode" danger onYes={() => void remove(deleting)} onNo={() => setDeleting(null)}>
          The episode and all its recorded lines are deleted for good. This can't be undone.
        </Confirm>
      )}
      {error && <Toast onClick={() => setError(null)}>{error}</Toast>}
    </Page>
  );
}
